import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FeedbackListQuery } from '@malash-office/types';
import {
  addFeedbackMessage,
  createFeedback,
  fetchAllFeedback,
  fetchFeedback,
  fetchFeedbackSummary,
  fetchMyFeedback,
  markFeedbackSeen,
  mergeFeedback,
  updateFeedback
} from './feedback.api';

export const FEEDBACK_KEY = 'feedback';

export const useMyFeedback = (enabled = true) =>
  useQuery({ queryKey: [FEEDBACK_KEY, 'mine'], queryFn: fetchMyFeedback, enabled });

export const useAllFeedback = (params: FeedbackListQuery, enabled: boolean) =>
  useQuery({ queryKey: [FEEDBACK_KEY, 'all', params], queryFn: () => fetchAllFeedback(params), enabled });

export const useFeedbackSummary = (enabled: boolean) =>
  useQuery({ queryKey: [FEEDBACK_KEY, 'summary'], queryFn: fetchFeedbackSummary, enabled });

export const useFeedback = (id: number | null) =>
  useQuery({ queryKey: [FEEDBACK_KEY, 'one', id], queryFn: () => fetchFeedback(id as number), enabled: !!id });

/** Every write refreshes the whole domain: the lists, the tiles and the open drawer all read it. */
const useFeedbackMutation = <TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [FEEDBACK_KEY] })
  });
};

export const useCreateFeedback = () => useFeedbackMutation(createFeedback);
export const useUpdateFeedback = () => useFeedbackMutation(updateFeedback);
export const useAddFeedbackMessage = () => useFeedbackMutation(addFeedbackMessage);
export const useMergeFeedback = () => useFeedbackMutation(mergeFeedback);
export const useMarkFeedbackSeen = () => useFeedbackMutation(markFeedbackSeen);
